import { trackEvent, trackPageView } from "./ga4";
import { trackCustom } from "./pixel";
import { errorToMessage, trackFrontendError } from "./frontendErrorTracker";

const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const PHONE_PATTERN = /(?:\+?\d[\d\s\-().]{8,}\d)/g;
const CARD_PATTERN = /\b(?:\d[ -]*?){13,19}\b/g;
const TOKEN_PATTERN = /\b(?:eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+)\b/g;
const LONG_NUMBER_PATTERN = /\b\d{7,}\b/g;
const SAFE_STRING_LEN = 80;
const HEARTBEAT_MS = 30000;
const CLICK_SAMPLE_RATE = 0.3;
const SCROLL_MILESTONES = [25, 50, 75, 90];
const SENSITIVE_FIELD_PATTERN = /(pass(word)?|pwd|secret|token|card|cvv|cvc|iban|routing|account|email|phone|name|address|street|zip|postal|social|ssn)/i;
const ENGAGED_CONVERSION_MS = 10000;
const ENGAGED_CONVERSION_EVENT = "Engaged10s";

let fetchPatchCount = 0;
let originalFetch = null;
const fetchEmitters = new Set();

const hasWindow = () => typeof window !== "undefined" && typeof document !== "undefined";

const safeNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const stripPII = (value) => (
  String(value || "")
    .replace(EMAIL_PATTERN, "[redacted_email]")
    .replace(PHONE_PATTERN, "[redacted_phone]")
    .replace(CARD_PATTERN, "[redacted_card]")
    .replace(TOKEN_PATTERN, "[redacted_token]")
    .replace(LONG_NUMBER_PATTERN, "[redacted_number]")
);

const sanitizeString = (value, maxLen = SAFE_STRING_LEN) => (
  stripPII(value)
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maxLen)
);

const sanitizeKey = (value) => sanitizeString(value, 40).toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "");

const sanitizePath = (rawUrl = "") => {
  try {
    const parsed = rawUrl.startsWith("http") ? new URL(rawUrl) : new URL(rawUrl, window.location.origin);
    const path = String(parsed.pathname || "/")
      .replace(/\/{2,}/g, "/")
      .replace(/\/$/, "") || "/";
    return sanitizeString(path, 80);
  } catch {
    return sanitizeString(String(rawUrl || "").split("?")[0] || "/", 80);
  }
};

const endpointGroup = (path = "") => {
  const p = String(path || "").toLowerCase();
  if (p.includes("/api/login")) return "auth_login";
  if (p.includes("/api/signup")) return "auth_signup";
  if (p.includes("/api/verify-token")) return "auth_verify";
  if (p.includes("/api/processcardinfo")) return "checkout_card";
  if (p.includes("/api/paypalsubscribed")) return "checkout_paypal";
  if (p.includes("/api/gethousingpoints")) return "search_paid";
  if (p.includes("/api/gettrialhousingpoints")) return "search_trial";
  if (p.includes("/api/llm/chat")) return "llm_chat";
  if (p.includes("/api/llm/history")) return "llm_history";
  if (p.includes("/api/analytics")) return "analytics";
  if (p.includes("/api/")) return "api_other";
  return "external";
};

const normalizeIntent = (label = "") => {
  const text = String(label || "").toLowerCase();
  if (!text) return "other";
  if (/(price|pricing|upgrade|plan|plus|max|checkout|pay|stripe|paypal|subscribe|trial)/.test(text)) return "pricing";
  if (/(signup|register|registrate|registrar|create account)/.test(text)) return "signup";
  if (/(login|log in|iniciar sesi)/.test(text)) return "login";
  if (/(search|buscar|polygon|coordinates|address)/.test(text)) return "search";
  if (/(export|download|csv|report)/.test(text)) return "export";
  if (/(contact|lead|mensaje|message|chat)/.test(text)) return "contact";
  if (/(tutorial|help|guide)/.test(text)) return "education";
  if (/(account|mi cuenta|membership|subscription)/.test(text)) return "account";
  if (/(logout|log out|sign out)/.test(text)) return "logout";
  if (/(privacy|terms|policy|cookies)/.test(text)) return "legal";
  return "other";
};

const getViewportClass = () => {
  if (!hasWindow()) return "unknown";
  if (window.innerWidth < 700) return "mobile";
  if (window.innerWidth < 1100) return "tablet";
  return "desktop";
};

const safeContext = (input = {}) => {
  if (!input || typeof input !== "object") return {};
  const out = {};
  Object.entries(input).forEach(([rawKey, rawValue]) => {
    const key = sanitizeKey(rawKey);
    if (!key) return;
    if (typeof rawValue === "string") {
      out[key] = sanitizeString(rawValue, 60);
      return;
    }
    if (typeof rawValue === "number") {
      if (Number.isFinite(rawValue)) out[key] = rawValue;
      return;
    }
    if (typeof rawValue === "boolean") {
      out[key] = rawValue;
    }
  });
  return out;
};

const extractClickableMetadata = (target) => {
  if (!target || !target.closest) return null;
  const node = target.closest("button, a, [role='button'], [data-analytics], .statButton, .searchBottom, .logOutBtn");
  if (!node) return null;

  const tag = String(node.tagName || "").toLowerCase();
  const id = sanitizeString(node.id || "", 30);
  const className = sanitizeString((node.className || "").toString().split(" ").slice(0, 2).join(" "), 40);
  const aria = sanitizeString(node.getAttribute("aria-label") || "", 50);
  const title = sanitizeString(node.getAttribute("title") || "", 50);
  const text = sanitizeString((node.textContent || "").slice(0, 80), 50);
  const label = aria || title || text || id || className || tag;
  const intent = normalizeIntent(label);

  let viewportSlot = "unknown";
  try {
    const rect = node.getBoundingClientRect();
    const ratio = rect.top / Math.max(1, window.innerHeight);
    if (ratio < 0.33) viewportSlot = "top";
    else if (ratio < 0.66) viewportSlot = "mid";
    else viewportSlot = "bottom";
  } catch {}

  return {
    tag,
    id,
    class_name: className,
    label,
    intent,
    viewport_slot: viewportSlot
  };
};

const getReferrerDomain = () => {
  try {
    if (!document.referrer) return "";
    return sanitizeString(new URL(document.referrer).hostname.replace(/^www\./, ""), 40);
  } catch {
    return "";
  }
};

const installFetchObserver = (emit) => {
  if (!hasWindow() || typeof window.fetch !== "function") return () => {};

  fetchEmitters.add(emit);
  fetchPatchCount += 1;

  if (fetchPatchCount === 1) {
    originalFetch = window.fetch.bind(window);

    window.fetch = async (input, init = {}) => {
      const startedAt = performance.now ? performance.now() : Date.now();
      const rawUrl = typeof input === "string" ? input : input?.url || "";
      const method = sanitizeString((init?.method || input?.method || "GET").toUpperCase(), 10);
      const endpoint = sanitizePath(rawUrl);
      const group = endpointGroup(endpoint);
      const rawUrlLower = String(rawUrl || "").toLowerCase();
      const skip =
        group === "analytics" ||
        rawUrlLower.includes("google-analytics.com") ||
        rawUrlLower.includes("googletagmanager.com") ||
        rawUrlLower.includes("doubleclick.net");

      const shouldSample = group !== "external" || Math.random() < 0.2;

      try {
        const response = await originalFetch(input, init);
        if (!skip && shouldSample) {
          const durationMs = Math.round((performance.now ? performance.now() : Date.now()) - startedAt);
          fetchEmitters.forEach((fn) => {
            try {
              fn("api_request_completed", {
                endpoint,
                endpoint_group: group,
                method,
                status: safeNumber(response.status) || 0,
                ok: Boolean(response.ok),
                duration_ms: durationMs
              });
            } catch {}
          });
        }
        if (!skip && !response.ok) {
          const isAuthValidationError =
            response.status >= 400 &&
            response.status < 500 &&
            (group === "auth_login" || group === "auth_signup" || group === "auth_verify");
          if (!isAuthValidationError) {
            trackFrontendError({
              type: response.status >= 500 ? "api_http_server_error" : "api_http_error",
              source: "fetch_response",
              message: `http_${response.status}`,
              endpoint,
              status: safeNumber(response.status),
              extra: {
                endpoint_group: group,
                method
              }
            });
          }
        }
        return response;
      } catch (error) {
        if (!skip) {
          const durationMs = Math.round((performance.now ? performance.now() : Date.now()) - startedAt);
          fetchEmitters.forEach((fn) => {
            try {
              fn("api_request_failed", {
                endpoint,
                endpoint_group: group,
                method,
                duration_ms: durationMs,
                error_name: sanitizeString(error?.name || "request_error", 40)
              });
            } catch {}
          });
          trackFrontendError({
            type: "api_network_error",
            source: "fetch_request",
            message: errorToMessage(error),
            endpoint,
            extra: {
              endpoint_group: group,
              method
            }
          });
        }
        throw error;
      }
    };
  }

  return () => {
    fetchEmitters.delete(emit);
    fetchPatchCount = Math.max(0, fetchPatchCount - 1);

    if (fetchPatchCount === 0 && originalFetch) {
      window.fetch = originalFetch;
      originalFetch = null;
    }
  };
};

const createVitalsObserver = (emit) => {
  if (!hasWindow() || typeof window.PerformanceObserver === "undefined") return () => {};

  let lcpMs = 0;
  let cls = 0;
  let inpMs = 0;
  let longTaskCount = 0;
  const observers = [];

  const observe = (type, handler) => {
    try {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach(handler);
      });
      observer.observe({ type, buffered: true });
      observers.push(observer);
    } catch {}
  };

  observe("largest-contentful-paint", (entry) => {
    lcpMs = Math.max(lcpMs, Math.round(entry.startTime || 0));
  });

  observe("layout-shift", (entry) => {
    if (!entry.hadRecentInput) {
      cls += Number(entry.value) || 0;
    }
  });

  observe("event", (entry) => {
    if (entry.interactionId) {
      inpMs = Math.max(inpMs, Math.round(entry.duration || 0));
    }
  });

  observe("longtask", () => {
    longTaskCount += 1;
  });

  const flush = () => {
    emit("web_vitals", {
      lcp_ms: lcpMs || undefined,
      cls: Number(cls.toFixed(3)) || undefined,
      inp_ms: inpMs || undefined,
      long_tasks: longTaskCount || undefined
    });
  };

  window.addEventListener("pagehide", flush, { once: true });

  return () => {
    observers.forEach((o) => {
      try { o.disconnect(); } catch {}
    });
    window.removeEventListener("pagehide", flush);
  };
};

const createNoopController = () => ({
  stop: () => {},
  trackVirtualPage: () => {},
  trackFunnelStage: () => {},
  trackMapInteraction: () => {},
  trackCustomAction: () => {}
});

export const startBehaviorTracking = ({ getContext } = {}) => {
  if (!hasWindow()) return createNoopController();

  const startedAt = Date.now();
  let engagedMs = 0;
  let visibleStartedAt = document.visibilityState === "visible" ? Date.now() : 0;
  let engagedConversionFired = false;
  let engagedConversionTimer = null;
  let lastPage = "";
  let lastViewportClass = getViewportClass();
  let lastClickSignature = "";
  let lastClickTs = 0;
  let mapTrackTs = 0;
  let rafScroll = null;
  const seenFunnelStages = new Set();
  const reachedScrollMilestones = new Set();
  const touchedForms = new Map();

  const clearEngagedConversionTimer = () => {
    if (engagedConversionTimer) {
      try { window.clearTimeout(engagedConversionTimer); } catch {}
      engagedConversionTimer = null;
    }
  };

  const emit = (eventName, params = {}) => {
    try {
      const context = safeContext(typeof getContext === "function" ? getContext() : {});
      trackEvent(eventName, {
        ...context,
        ...params
      });
    } catch {}
  };

  const calcSessionSec = () => Math.max(1, Math.round((Date.now() - startedAt) / 1000));
  const calcEngagedSec = () => Math.max(0, Math.round(engagedMs / 1000));
  const calcEngagedMsNow = () => {
    if (visibleStartedAt > 0) return engagedMs + Math.max(0, Date.now() - visibleStartedAt);
    return engagedMs;
  };

  const updateEngagedTime = () => {
    if (visibleStartedAt > 0) {
      engagedMs += Math.max(0, Date.now() - visibleStartedAt);
    }
    visibleStartedAt = document.visibilityState === "visible" ? Date.now() : 0;
  };

  const fireEngagedConversion = (source = "timer") => {
    if (engagedConversionFired) return;
    engagedConversionFired = true;
    clearEngagedConversionTimer();

    const engagedSecNow = Math.max(0, Math.round(calcEngagedMsNow() / 1000));
    const context = safeContext(typeof getContext === "function" ? getContext() : {});
    const routeAtFire = sanitizeKey(context.route || lastPage || "unknown_route") || "unknown_route";

    try {
      trackCustom(ENGAGED_CONVERSION_EVENT, {
        ...context,
        route_at_fire: routeAtFire,
        viewport_class: lastViewportClass,
        engaged_sec: engagedSecNow,
        threshold_sec: Math.round(ENGAGED_CONVERSION_MS / 1000),
        source
      });
    } catch {}

    try {
      emit("engaged_10s_reached", {
        engaged_sec: engagedSecNow,
        threshold_sec: Math.round(ENGAGED_CONVERSION_MS / 1000),
        source,
        route_at_fire: routeAtFire
      });
    } catch {}
  };

  const maybeFireEngagedConversion = (source = "fallback") => {
    if (engagedConversionFired) return;
    if (calcEngagedMsNow() < ENGAGED_CONVERSION_MS) return;
    fireEngagedConversion(source);
  };

  const scheduleEngagedConversion = () => {
    if (engagedConversionFired) return;
    clearEngagedConversionTimer();
    if (document.visibilityState !== "visible") return;

    const remaining = ENGAGED_CONVERSION_MS - calcEngagedMsNow();
    if (remaining <= 0) {
      maybeFireEngagedConversion("immediate");
      return;
    }

    engagedConversionTimer = window.setTimeout(() => {
      // Only count time while tab is visible.
      if (document.visibilityState !== "visible") return;
      updateEngagedTime();
      maybeFireEngagedConversion("timer");
      scheduleEngagedConversion();
    }, remaining);
  };

  const flushAbandonedForms = (reason = "session_end") => {
    touchedForms.forEach((meta, formId) => {
      if (meta.submitted || meta.abandoned) return;
      const dwellSec = Math.max(0, Math.round((Date.now() - meta.startedAt) / 1000));
      if (dwellSec < 2) return;
      meta.abandoned = true;
      emit("form_abandoned", {
        form_id: formId,
        fields_touched: meta.fieldsTouched,
        dwell_sec: dwellSec,
        reason
      });
    });
  };

  const trackTimingFromNavigation = () => {
    try {
      const nav = performance.getEntriesByType("navigation")[0];
      if (!nav) return;
      emit("page_timing", {
        ttfb_ms: Math.round(nav.responseStart || 0),
        dom_content_loaded_ms: Math.round(nav.domContentLoadedEventEnd || 0),
        page_load_ms: Math.round(nav.loadEventEnd || 0)
      });
    } catch {}
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      clearEngagedConversionTimer();
      updateEngagedTime();
      maybeFireEngagedConversion("visibility_hidden");
      emit("app_visibility_changed", {
        state: "hidden",
        engaged_sec: calcEngagedSec()
      });
      flushAbandonedForms("hidden");
      return;
    }

    visibleStartedAt = Date.now();
    emit("app_visibility_changed", { state: "visible" });
    scheduleEngagedConversion();
  };

  const onFocus = () => {
    visibleStartedAt = Date.now();
    emit("window_focus");
    scheduleEngagedConversion();
  };

  const onBlur = () => {
    clearEngagedConversionTimer();
    updateEngagedTime();
    maybeFireEngagedConversion("window_blur");
    emit("window_blur", { engaged_sec: calcEngagedSec() });
  };

  const onError = (event) => {
    const routeName = sanitizeString(
      (typeof getContext === "function" ? getContext()?.route : "") || lastPage || "unknown_route",
      50
    );
    const sourcePath = sanitizePath(event?.filename || "");
    const line = safeNumber(event?.lineno) || undefined;
    const column = safeNumber(event?.colno) || undefined;
    const message = sanitizeString(event?.message || "runtime_error", 70);

    emit("frontend_error", {
      error_type: "runtime",
      message,
      source_path: sourcePath,
      line,
      column
    });

    trackFrontendError({
      type: "runtime",
      source: "window_error",
      message,
      route: routeName,
      sourcePath,
      line,
      column
    });
  };

  const onUnhandledRejection = (event) => {
    const reason = event?.reason;
    const routeName = sanitizeString(
      (typeof getContext === "function" ? getContext()?.route : "") || lastPage || "unknown_route",
      50
    );
    const message = sanitizeString(errorToMessage(reason), 70);

    emit("frontend_error", {
      error_type: "promise_rejection",
      message
    });

    trackFrontendError({
      type: "promise_rejection",
      source: "window_unhandled_rejection",
      message,
      route: routeName,
      extra: {
        reason_name: sanitizeString(reason?.name || typeof reason, 25)
      }
    });
  };

  const onClick = (event) => {
    const meta = extractClickableMetadata(event?.target);
    if (!meta) return;

    const signature = `${meta.intent}|${meta.id}|${meta.label}`;
    const now = Date.now();
    if (lastClickSignature === signature && now - lastClickTs < 700) return;
    lastClickSignature = signature;
    lastClickTs = now;

    const isHighIntent = meta.intent !== "other";
    if (!isHighIntent && Math.random() > CLICK_SAMPLE_RATE) return;

    emit(isHighIntent ? "cta_click" : "ui_click", {
      intent: meta.intent,
      element_tag: meta.tag,
      element_id: meta.id || undefined,
      element_class: meta.class_name || undefined,
      element_label: meta.label || undefined,
      viewport_slot: meta.viewport_slot
    });
  };

  const onFormFocusIn = (event) => {
    const target = event?.target;
    if (!target || !target.closest) return;
    const form = target.closest("form");
    if (!form) return;

    const fieldName = String(target.name || target.id || "").trim();
    const formId = sanitizeString(form.getAttribute("id") || form.getAttribute("name") || form.getAttribute("action") || "form", 40);
    if (!formId) return;

    if (!touchedForms.has(formId)) {
      touchedForms.set(formId, {
        startedAt: Date.now(),
        fieldsTouched: 0,
        submitted: false,
        abandoned: false,
        hasSensitiveField: false
      });
      emit("form_started", {
        form_id: formId,
        field_count: safeNumber(form.elements?.length) || undefined
      });
    }

    const formMeta = touchedForms.get(formId);
    formMeta.fieldsTouched += 1;
    formMeta.hasSensitiveField = formMeta.hasSensitiveField || SENSITIVE_FIELD_PATTERN.test(fieldName);
  };

  const onFormSubmit = (event) => {
    const form = event?.target;
    if (!form) return;
    const formId = sanitizeString(form.getAttribute("id") || form.getAttribute("name") || form.getAttribute("action") || "form", 40);
    const meta = touchedForms.get(formId);
    const dwellSec = meta ? Math.max(0, Math.round((Date.now() - meta.startedAt) / 1000)) : undefined;

    if (meta) {
      meta.submitted = true;
    }

    emit("form_submitted", {
      form_id: formId,
      field_count: safeNumber(form.elements?.length) || undefined,
      fields_touched: meta?.fieldsTouched || undefined,
      dwell_sec: dwellSec,
      has_sensitive_fields: meta?.hasSensitiveField || false
    });
  };

  const handleScrollMilestones = () => {
    rafScroll = null;
    const root = document.scrollingElement || document.documentElement;
    if (!root) return;
    const maxScroll = Math.max(1, root.scrollHeight - root.clientHeight);
    const pct = Math.round((root.scrollTop / maxScroll) * 100);
    SCROLL_MILESTONES.forEach((m) => {
      if (pct >= m && !reachedScrollMilestones.has(m)) {
        reachedScrollMilestones.add(m);
        emit("scroll_depth_reached", { depth_pct: m });
      }
    });
  };

  const onScroll = () => {
    if (rafScroll) return;
    rafScroll = window.requestAnimationFrame(handleScrollMilestones);
  };

  const onResize = () => {
    const nextClass = getViewportClass();
    if (nextClass === lastViewportClass) return;
    lastViewportClass = nextClass;
    emit("viewport_changed", {
      viewport_class: nextClass,
      viewport_w: safeNumber(window.innerWidth),
      viewport_h: safeNumber(window.innerHeight)
    });
  };

  const flushSessionSummary = (reason = "pagehide") => {
    updateEngagedTime();
    maybeFireEngagedConversion(`session_summary_${sanitizeKey(reason) || "pagehide"}`);
    flushAbandonedForms(reason);
    emit("session_summary", {
      reason,
      session_sec: calcSessionSec(),
      engaged_sec: calcEngagedSec(),
      engagement_rate: Number((calcEngagedSec() / Math.max(1, calcSessionSec())).toFixed(2))
    });
  };

  const onBeforeUnload = () => flushSessionSummary("beforeunload");
  const onPageHide = () => flushSessionSummary("pagehide");

  const heartbeat = window.setInterval(() => {
    if (document.visibilityState !== "visible") return;
    updateEngagedTime();
    emit("engagement_heartbeat", {
      session_sec: calcSessionSec(),
      engaged_sec: calcEngagedSec()
    });
  }, HEARTBEAT_MS);

  const removeFetchObserver = installFetchObserver(emit);
  const stopVitalsObserver = createVitalsObserver(emit);

  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("focus", onFocus);
  window.addEventListener("blur", onBlur);
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onUnhandledRejection);
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onResize, { passive: true });
  window.addEventListener("beforeunload", onBeforeUnload);
  window.addEventListener("pagehide", onPageHide);
  document.addEventListener("click", onClick, true);
  document.addEventListener("focusin", onFormFocusIn, true);
  document.addEventListener("submit", onFormSubmit, true);

  trackTimingFromNavigation();

  emit("session_started", {
    viewport_class: lastViewportClass,
    language: sanitizeString(navigator.language || "", 20),
    timezone: sanitizeString(Intl.DateTimeFormat().resolvedOptions()?.timeZone || "", 30),
    referrer_domain: getReferrerDomain() || undefined
  });
  scheduleEngagedConversion();

  const trackVirtualPage = (pageName, params = {}) => {
    const route = sanitizeKey(pageName || "unknown_route");
    if (!route || route === lastPage) return;
    lastPage = route;

    trackPageView(`CASADATA ${route.replace(/_/g, " ")}`, {
      app_route: route,
      ...safeContext(typeof getContext === "function" ? getContext() : {}),
      ...params
    });

    emit("app_route_change", {
      route,
      ...params
    });
  };

  const trackFunnelStage = (stage, params = {}) => {
    const stageKey = sanitizeKey(stage || "stage");
    if (!stageKey || seenFunnelStages.has(stageKey)) return;
    seenFunnelStages.add(stageKey);
    emit("funnel_stage_reached", {
      stage: stageKey,
      ...params
    });
  };

  const trackMapInteraction = (interactionType, params = {}) => {
    const now = Date.now();
    if (now - mapTrackTs < 1200) return;
    mapTrackTs = now;
    emit("map_interaction", {
      interaction_type: sanitizeKey(interactionType || "interaction"),
      ...params
    });
  };

  const trackCustomAction = (actionName, params = {}) => {
    emit(sanitizeKey(actionName || "custom_action") || "custom_action", params);
  };

  const stop = () => {
    clearEngagedConversionTimer();
    window.clearInterval(heartbeat);
    if (rafScroll) {
      try { window.cancelAnimationFrame(rafScroll); } catch {}
    }
    flushSessionSummary("manual_stop");

    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("focus", onFocus);
    window.removeEventListener("blur", onBlur);
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onUnhandledRejection);
    window.removeEventListener("scroll", onScroll);
    window.removeEventListener("resize", onResize);
    window.removeEventListener("beforeunload", onBeforeUnload);
    window.removeEventListener("pagehide", onPageHide);
    document.removeEventListener("click", onClick, true);
    document.removeEventListener("focusin", onFormFocusIn, true);
    document.removeEventListener("submit", onFormSubmit, true);

    try { removeFetchObserver(); } catch {}
    try { stopVitalsObserver(); } catch {}
  };

  return {
    stop,
    trackVirtualPage,
    trackFunnelStage,
    trackMapInteraction,
    trackCustomAction
  };
};

export default startBehaviorTracking;
